import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Text } from '@chakra-ui/react';

import { CustomIcon } from '../../icon';
import type { AuthenticationMethod } from '../store';
import {
  useNabanStoreSelectedAuthenticationMethod,
  useNabanStoreSetAuthenticationMethod,
} from '../store';

type Props = {
  item: AuthenticationMethod;
  testId?: string;
  onItemSelection?: (item: AuthenticationMethod) => void;
};

export const AuthenticationMethodsListItem = ({ item, testId, onItemSelection }: Props) => {
  const { t } = useTranslation('naban');

  const selectedAuthenticationMethod = useNabanStoreSelectedAuthenticationMethod();
  const setAuthenticationMethod = useNabanStoreSetAuthenticationMethod();

  const title = useMemo(() => {
    if (item.type === 'personal') {
      return t('authenticationMethods.selector.options.personal');
    }

    if (item.type === 'company') {
      return t('authenticationMethods.selector.options.company', { name: item.name });
    }

    if (item.type === 'companyAndSisterCompanies') {
      return t('authenticationMethods.selector.options.subsidiaryCompanies', { name: item.name });
    }

    return t('authenticationMethods.selector.options.heir', {
      name: `${item.firstName} ${item.lastName}`,
    });
  }, [item, t]);

  const onClick = useCallback(() => {
    setAuthenticationMethod(item);
    onItemSelection?.(item);
  }, [item, setAuthenticationMethod, onItemSelection]);

  const isActive =
    selectedAuthenticationMethod?.token && selectedAuthenticationMethod.token === item.token;

  return (
    <Flex as='button' width='100%' onClick={onClick} py={3} data-testid={testId}>
      <Flex flex={1} justifyContent='space-between' alignItems='center'>
        <Flex flexDirection='row' alignItems='center' gap={2}>
          {item.type === 'companyAndSisterCompanies' && (
            <CustomIcon name='cornerDownRight' color='neutral600' />
          )}
          <Text
            textAlign='left'
            variant='tableCell'
            textTransform='initial'
            fontSize={{ base: '16px', md: '18px' }}
            letterSpacing='initial'>
            {title}
          </Text>
        </Flex>
        {isActive ? (
          <Text
            textTransform='uppercase'
            borderColor='neutral300'
            borderRadius='md'
            borderWidth={1}
            variant='small'
            fontSize='.75rem'
            px={2.5}
            py={0.5}>
            {t('authenticationMethods.selector.active.label')}
          </Text>
        ) : (
          <CustomIcon name='chevron-right' color='neutral600' />
        )}
      </Flex>
    </Flex>
  );
};
